import { useState, useSyncExternalStore, type FormEvent } from "react";
import { useReverification, useSession, useUser } from "@clerk/react";
import { Copy, Download, KeyRound } from "lucide-react";
import QRCode from "qrcode";

import { Button } from "../primitives/Button";
import { FieldShell, TextField } from "../primitives/Field";
import { PREVIEW_TOTP_USER } from "../../lib/auth/setup-security-preview";

export type Enrollment = {
  qrDataUrl: string | null;
  secret: string | null;
  uri: string | null;
};

type TOTPSetupResource = { secret?: string; uri?: string };

export type AuthenticatorSetupComplete = {
  backupCodes: string[];
  userId: string;
};

const TOTP_CODE_PATTERN = /^\d{6}$/;

const COARSE_POINTER = "(pointer: coarse)";

/** Touch-primary device (no user-agent sniffing). False where matchMedia is absent. */
function useCoarsePointer(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const mql = window.matchMedia(COARSE_POINTER);
      mql.addEventListener?.("change", onChange);
      return () => mql.removeEventListener?.("change", onChange);
    },
    () =>
      typeof window !== "undefined" && !!window.matchMedia
        ? window.matchMedia(COARSE_POINTER).matches
        : false,
    () => false,
  );
}

type TotpUser = {
  id: string;
  createTOTP: () => Promise<unknown>;
  verifyTOTP: (a: { code: string }) => Promise<{ backupCodes?: string[] | null }>;
  reload: () => Promise<unknown>;
};

type TotpSource = () => {
  user: TotpUser | null;
  session: { reload: () => Promise<unknown> } | null;
  createTOTP: () => Promise<unknown>;
};

/** Clerk-backed user + reverified createTOTP. */
const useClerkTotp: TotpSource = () => {
  const { user } = useUser();
  const { session } = useSession();
  const createTOTP = useReverification(async () => user?.createTOTP());
  return {
    user: (user as TotpUser | null | undefined) ?? null,
    session: session ?? null,
    createTOTP,
  };
};

/** Fixture source for the DEV preview route: no Clerk, no network. */
const usePreviewTotp: TotpSource = () => ({
  user: PREVIEW_TOTP_USER,
  session: null,
  createTOTP: () => PREVIEW_TOTP_USER.createTOTP(),
});

export interface AuthenticatorSetupPanelProps {
  /** Fires once the code verifies (before the pro has acknowledged the backup
   *  codes). The clean seam for analytics. */
  onVerified?: (result: AuthenticatorSetupComplete) => void;
  /** Fires when the pro confirms they saved the backup codes. */
  onComplete?: (result: AuthenticatorSetupComplete) => void;
  onCancel?: () => void;
  startLabel?: string;
  /** "center" lays every state out on one centered column (the setup screen);
   *  "start" (default) keeps the Settings card layout. */
  align?: "center" | "start";
  /** DEV preview route only: serve local fixtures instead of Clerk. The mode
   *  is fixed per mount (it picks the hook source), never toggled live. */
  preview?: boolean;
  /** Start already enrolled (QR / manual key / code input). DEV preview only;
   *  production callers pass nothing. */
  initialEnrollment?: Enrollment;
}

/**
 * Authenticator-app (TOTP) enrollment: createTOTP → QR + manual key → verify a
 * 6-digit code → backup codes shown once (copy / download) → Continue.
 * Ported from the consumer panel's Clerk sequence on pro DS primitives.
 */
export function AuthenticatorSetupPanel(props: AuthenticatorSetupPanelProps) {
  return props.preview ? (
    <SetupPanelBody {...props} useTotp={usePreviewTotp} />
  ) : (
    <SetupPanelBody {...props} useTotp={useClerkTotp} />
  );
}

function SetupPanelBody({
  useTotp,
  initialEnrollment,
  onVerified,
  onComplete,
  onCancel,
  startLabel = "Set up authenticator app",
  align = "start",
}: AuthenticatorSetupPanelProps & { useTotp: TotpSource }) {
  const centered = align === "center";
  const { user, session, createTOTP } = useTotp();
  const [enrollment, setEnrollment] = useState<Enrollment | null>(
    () => initialEnrollment ?? null,
  );
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const isMobile = useCoarsePointer();

  async function begin() {
    if (!user || isCreating) return;
    setIsCreating(true);
    setError(null);
    try {
      const totp = (await createTOTP()) as TOTPSetupResource | undefined;
      if (!totp) throw new Error("Couldn't start authenticator setup. Try again.");
      setEnrollment({
        qrDataUrl: totp.uri
          ? await QRCode.toDataURL(totp.uri, { margin: 1, width: 192 })
          : null,
        secret: totp.secret ?? null,
        uri: totp.uri ?? null,
      });
      setShowQr(false);
      setCode("");
    } catch (err) {
      setError(clerkErrorMessage(err, "Couldn't start authenticator setup. Try again."));
    } finally {
      setIsCreating(false);
    }
  }

  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || isVerifying) return;
    if (!TOTP_CODE_PATTERN.test(code)) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setIsVerifying(true);
    setError(null);
    try {
      const verified = await user.verifyTOTP({ code });
      const codes = verified.backupCodes ?? [];
      setEnrollment(null);
      setCode("");
      setBackupCodes(codes);
      await Promise.all([
        user.reload().catch(() => undefined),
        session?.reload().catch(() => undefined),
      ]);
      onVerified?.({ backupCodes: codes, userId: user.id });
    } catch (err) {
      setError(
        clerkErrorMessage(err, "Couldn't verify that code. Check the code and try again."),
      );
    } finally {
      setIsVerifying(false);
    }
  }

  if (backupCodes) {
    return (
      <div className="grid gap-3" role="status">
        <BackupCodesList
          codes={backupCodes}
          onCopied={setCopied}
          copied={copied}
          align={align}
        />
        <div className={centered ? "flex justify-center" : "flex justify-end"}>
          <Button
            onClick={() =>
              onComplete?.({ backupCodes, userId: user?.id ?? "" })
            }
          >
            I've saved these codes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      {!enrollment ? (
        <div className={centered ? "flex justify-center" : undefined}>
          <Button disabled={!user || isCreating} onClick={() => void begin()}>
            <KeyRound className="size-4" />
            {isCreating ? "Opening…" : startLabel}
          </Button>
        </div>
      ) : (
        <form
          className={`ph-no-capture grid gap-4${centered ? " text-center" : ""}`}
          onSubmit={verify}
        >
          <div
            className={
              centered
                ? "grid justify-items-center gap-4"
                : isMobile && !showQr && enrollment.uri
                  ? "grid gap-4"
                  : "grid gap-4 md:grid-cols-[12rem_minmax(0,1fr)]"
            }
          >
            {!isMobile || showQr || !enrollment.uri ? (
              <div className="flex min-h-48 items-center justify-center rounded-lg bg-card p-3">
                {enrollment.qrDataUrl ? (
                  <img
                    alt="Authenticator app QR code"
                    className="size-40"
                    src={enrollment.qrDataUrl}
                  />
                ) : (
                  <p className="text-center text-sm text-fg-muted">
                    Use the manual setup key.
                  </p>
                )}
              </div>
            ) : null}
            <div
              className={
                centered
                  ? "grid w-full content-start justify-items-center gap-3"
                  : "grid content-start gap-3"
              }
            >
              <p className="text-sm text-fg-muted">
                {isMobile && enrollment.uri
                  ? "Add Sill to your authenticator app, then enter the 6-digit code it shows."
                  : "Open your authenticator app, add a new account, scan the code, then enter the 6-digit code it shows for Sill."}
              </p>
              {enrollment.uri ? (
                <div className={centered ? "grid justify-items-center gap-1" : "grid gap-1"}>
                  <a
                    className={
                      isMobile
                        ? "btn btn-default justify-center"
                        : "text-sm font-medium underline"
                    }
                    href={enrollment.uri}
                  >
                    Add to authenticator app
                  </a>
                  <p className="field-hint">
                    {isMobile
                      ? "Opens your phone's password or authenticator app."
                      : "Or open in an authenticator app on this device."}
                  </p>
                  {isMobile ? (
                    <button
                      className={
                        centered
                          ? "justify-self-center text-sm underline"
                          : "justify-self-start text-sm underline"
                      }
                      type="button"
                      onClick={() => setShowQr((v) => !v)}
                    >
                      {showQr ? "Hide QR code" : "Show QR code instead"}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {enrollment.secret ? (
                <div className={centered ? "w-full max-w-xs" : undefined}>
                  <p className="field-label">Manual setup key</p>
                  <code className="block break-all rounded-lg bg-card px-3 py-2 font-mono text-sm">
                    {formatSecret(enrollment.secret)}
                  </code>
                </div>
              ) : null}
            </div>
          </div>
          <div className={centered ? "mx-auto w-full max-w-xs" : undefined}>
          <FieldShell
            label="Verification code"
            hint="Use the current 6-digit code from your authenticator app."
          >
            <TextField
              aria-label="Verification code"
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              pattern="\d{6}"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
            />
          </FieldShell>
          </div>
          <div className={centered ? "flex justify-center gap-2" : "flex justify-end gap-2"}>
            {onCancel ? (
              <Button
                variant="outline"
                disabled={isVerifying}
                onClick={() => {
                  setEnrollment(null);
                  setCode("");
                  setError(null);
                  onCancel();
                }}
              >
                Cancel
              </Button>
            ) : null}
            <Button
              type="submit"
              disabled={!TOTP_CODE_PATTERN.test(code) || isVerifying}
            >
              {isVerifying ? "Verifying…" : "Verify and enable"}
            </Button>
          </div>
        </form>
      )}
      {error ? (
        <p
          className={`text-sm text-destructive${centered ? " text-center" : ""}`}
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Backup codes with copy + download. Shown once; shared with the Security card. */
export function BackupCodesList({
  codes,
  copied,
  onCopied,
  align = "start",
}: {
  codes: string[];
  copied?: boolean;
  onCopied?: (copied: boolean) => void;
  align?: "center" | "start";
}) {
  const centered = align === "center";
  const [localCopied, setLocalCopied] = useState(false);
  const isCopied = copied ?? localCopied;
  const text = codes.join("\n");

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      (onCopied ?? setLocalCopied)(true);
    } catch {
      // Clipboard denied — the codes stay visible and downloadable.
    }
  }

  function download() {
    const url = URL.createObjectURL(new Blob([text + "\n"], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "sill-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div
      className={`ph-no-capture grid gap-3${
        centered ? " mx-auto w-full max-w-xs text-center" : ""
      }`}
    >
      <div>
        <p className="text-sm font-semibold">Backup codes</p>
        <p className="field-hint">
          Store these somewhere safe. Each code works once, and they won't be
          shown again.
        </p>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {codes.map((c) => (
          <li key={c} className="rounded-lg bg-card px-3 py-2 font-mono text-sm">
            {c}
          </li>
        ))}
      </ul>
      <div className={centered ? "flex justify-center gap-2" : "flex gap-2"}>
        <Button variant="outline" size="sm" onClick={() => void copy()}>
          <Copy className="size-4" />
          {isCopied ? "Copied" : "Copy"}
        </Button>
        <Button variant="outline" size="sm" onClick={download}>
          <Download className="size-4" />
          Download
        </Button>
      </div>
    </div>
  );
}

function formatSecret(secret: string) {
  return secret.replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();
}

export function clerkErrorMessage(err: unknown, fallback: string) {
  if (typeof err === "object" && err !== null && "errors" in err) {
    const errors = (
      err as { errors?: Array<{ longMessage?: string; message?: string }> }
    ).errors;
    const message = errors?.[0]?.longMessage ?? errors?.[0]?.message;
    if (message) return message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
